// [APPRO-STOCK] Dialogs for stock restocking and movement history
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { PackagePlus, Loader2, History, ArrowUpCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { getFeatures } from "../../lib/boutiqueType";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return iso; }
};

// ─── Restock Dialog ───────────────────────────────────────────────────────────
export function RestockDialog({ open, onOpenChange, product, devise, getAuthHeaders, onDone }) {
  const [variantId, setVariantId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [supplier, setSupplier] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && product) {
      setVariantId(product.variants?.[0]?.id || "");
      setQuantity("");
      setSupplier("");
      setPurchasePrice("");
      setNote("");
    }
  }, [open, product]);

  const handleSubmit = async () => {
    const qty = parseInt(quantity);
    if (!variantId) { toast.error("Sélectionnez une variante"); return; }
    if (!qty || qty <= 0) { toast.error("Quantité invalide"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/stock/restock`, {
        product_id: product.id,
        variant_id: variantId,
        quantity: qty,
        supplier: supplier || null,
        note: note || null,
        purchase_price: purchasePrice ? parseFloat(purchasePrice) : null
      }, getAuthHeaders());
      toast.success(`Stock mis à jour : ${res.data.new_quantity} en stock`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'approvisionnement");
    } finally {
      setSaving(false);
    }
  };

  if (!product) return null;
  const showVariantSelect = getFeatures().variants || (product.variants?.length || 0) > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="restock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="w-5 h-5 text-green-600" />
            Approvisionner le stock
          </DialogTitle>
          <DialogDescription>
            {product.name}
            {!showVariantSelect && ` — ${product.variants?.[0]?.quantity ?? 0} en stock actuellement`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {showVariantSelect && (
          <div className="space-y-2">
            <Label>Variante *</Label>
            <Select value={variantId} onValueChange={setVariantId}>
              <SelectTrigger data-testid="restock-variant-select">
                <SelectValue placeholder="Choisir..." />
              </SelectTrigger>
              <SelectContent>
                {product.variants?.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.size} · {v.color} — {v.quantity} en stock
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Quantité à ajouter *</Label>
              <Input type="number" min="1" value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0" data-testid="restock-quantity-input" />
            </div>
            <div className="space-y-2">
              <Label>Prix d'achat ({devise})</Label>
              <Input type="number" min="0" step="0.01" value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                placeholder="Optionnel" data-testid="restock-price-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fournisseur</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)}
              placeholder="Nom du fournisseur (optionnel)" data-testid="restock-supplier-input" />
          </div>
          <div className="space-y-2">
            <Label>Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Remarque (optionnel)" data-testid="restock-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving}
            className="bg-green-600 hover:bg-green-700 text-white" data-testid="restock-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackagePlus className="w-4 h-4 mr-2" />}
            Ajouter au stock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Stock History Dialog ─────────────────────────────────────────────────────
export function StockHistoryDialog({ open, onOpenChange, getAuthHeaders, devise }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      axios.get(`${API}/stock/movements?limit=200`, getAuthHeaders())
        .then((res) => setMovements(res.data.movements || []))
        .catch(() => toast.error("Erreur lors du chargement de l'historique"))
        .finally(() => setLoading(false));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="stock-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Historique des approvisionnements
          </DialogTitle>
          <DialogDescription>Mouvements de stock enregistrés (qui, quand, combien)</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 py-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : movements.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">
              Aucun approvisionnement enregistré pour le moment
            </p>
          ) : (
            movements.map((m) => (
              <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg border bg-secondary/30"
                data-testid={`movement-${m.id}`}>
                <ArrowUpCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{m.product_name}</span>
                    {m.variant_label && (
                      <Badge variant="outline" className="text-xs">{m.variant_label}</Badge>
                    )}
                    <Badge className="bg-green-600 text-xs">+{m.quantity}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Par <span className="font-medium">{m.author?.name || "Inconnu"}</span>
                    {m.supplier ? ` · Fournisseur : ${m.supplier}` : ""}
                    {m.purchase_price ? ` · ${m.purchase_price} ${devise}/unité` : ""}
                  </p>
                  {m.note && <p className="text-xs text-muted-foreground italic mt-0.5">{m.note}</p>}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(m.created_at)}</span>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
